/**
 * Modello "ceiling section" semantico — sezione tecnica orizzontale del controsoffitto.
 *
 * A differenza della parete (impilamento verticale di lastre + struttura), il
 * controsoffitto è un impilamento ORIZZONTALE letto dall'alto verso il basso:
 *
 *   1. SOLAIO (datum, esistente — non è un layer della stratigrafia)
 *   2. CAMERA TECNICA + pendini verticali (gap = ceiling_drop_mm)
 *   3. ORDITURA PRIMARIA (profili C/T visti di taglio, passo primary_spacing_mm)
 *   4. ORDITURA SECONDARIA perpendicolare (solo per orditura doppia)
 *   5. ISOLANTE (opzionale, sopra orditura)
 *   6. LASTRE (filo basso visibile)
 *
 * Il modello aggrega i layer raw + lo stato dimensionale `CeilingDimensionalState`
 * in "blocchi visuali" pronti per il renderer SVG. Riusa `BoardBlock` e
 * `classifyMaterial` da `wallModel.ts` per coerenza con la sezione parete.
 *
 * Convenzione orientamento: i blocchi sono ordinati TOP→BOTTOM (solaio per primo,
 * lastra per ultima). Il renderer SVG impila i blocchi lungo Y crescente.
 */

import type { LayerV2 } from '../../types';
import type { CeilingDimensionalState } from '../../hooks/useCeilingConfiguratorState';
import {
  classifyMaterial,
  type BoardBlock,
  type DrawingMaterialKind,
} from './wallModel';

// Re-export per comodità dei consumer
export type { BoardBlock, DrawingMaterialKind };

// ============================================================================
// Tipi blocchi ceiling-specific
// ============================================================================

/** Solaio esistente — datum, non è un layer della stratigrafia. Rappresentato come fascia tratteggiata. */
export interface SlabBlock {
  kind: 'slab';
  /** Spessore visivo del datum (mm, fisso a 80 — è solo decorativo). */
  thicknessMm: number;
  label: string; // "Solaio esistente"
}

/**
 * Camera tecnica (gap pendinatura) + pendini visibili. Lo spessore è `ceiling_drop_mm`.
 * Se attachment_type='fixed' lo spessore è 0 — il blocco viene omesso.
 */
export interface HangerSpaceBlock {
  kind: 'hanger_space';
  /** Altezza camera tecnica = ceiling_drop_mm (mm). */
  depthMm: number;
  /**
   * Spacing pendini lungo l'orditura primaria (mm). Usato dal renderer SVG per
   * disegnare N pendini equispaziati nella vista sezione.
   */
  hangerSpacingPrimaryMm: number;
  /** Spacing perpendicolare (mm) — utile solo per la vista PIANTA RIFLESSA. */
  hangerSpacingPerpendicularMm: number;
  /** Etichetta callout — es. "Camera tecnica 200 mm — pendini ogni 900 mm". */
  label: string;
}

/**
 * Blocco orditura (primaria o secondaria). Rappresentato come sequenza di
 * profili C/T visti di taglio (rettangoli ravvicinati con passo costante).
 */
export interface FrameBlock {
  kind: 'frame';
  /** 'primary' = orditura primaria (passo primary_spacing_mm) | 'secondary'. */
  level: 'primary' | 'secondary';
  /** Profondità del blocco = altezza profilo (mm). Letta da material.thickness o material.width. */
  depthMm: number;
  /** Layer del profilo (T15/T24, C Plus, asolato U, ecc.). */
  layer: LayerV2;
  /** Passo profili (mm) — usato per disegnare N profili nella vista. */
  spacingMm: number;
  fillId: string;
  label: string;
}

/** Isolante per ceiling — può essere SOPRA orditura primaria o NEL VANO secondaria. */
export interface CeilingInsulationBlock {
  kind: 'ceiling_insulation';
  layer: LayerV2;
  depthMm: number;
  fillId: string;
  label: string;
  /**
   * Posizione semantica: 'above_primary' (più comune, sopra orditura primaria) o
   * 'in_secondary_gap' (raro, dentro vano orditura secondaria — solo per doppia orditura).
   */
  position: 'above_primary' | 'in_secondary_gap';
}

/** Blocco generico riusato per layer non-strutturali (accessory/finish): non disegnato ma riportato in legenda. */
export type CeilingBlock = SlabBlock | HangerSpaceBlock | FrameBlock | CeilingInsulationBlock | BoardBlock;

export interface CeilingSectionModel {
  /** Blocchi ordinati TOP→BOTTOM (solaio per primo, lastra finale per ultima). */
  blocks: CeilingBlock[];
  /** Spessore totale visibile (mm) = drop + struttura + isolante + lastre. */
  totalDepthMm: number;
  /** True se ha orditura doppia (primary + secondary). */
  hasDoubleStructure: boolean;
  /** True se è pendinato (attachment_type='suspended'). */
  isSuspended: boolean;
  /** Numero pendini stimato per m² (per il preview costi). NULL se attachment_type='fixed'. */
  hangerCountPerSqm: number | null;
  /** Conteggio lastre nel pacchetto (utile per labeling). */
  boardCount: number;
}

// ============================================================================
// Classifier specifico per profili controsoffitto
// ============================================================================

/**
 * Determina se un profilo è "primario" (T portante, C primario, omega, asolato U
 * per controsoffitti tecnici) o "secondario" (C secondario, profilo perpendicolare).
 *
 * Heuristic basato su material_type e profile_type (popolati dai classifier import).
 * Quando double-structure, l'ordine dei layer nella stratigrafia determina il level
 * (primo profilo trovato dopo lo slab = primario, secondo = secondario).
 */
function isProfileType(layer: LayerV2): boolean {
  const cat = layer.material?.category;
  return cat === 'structure_frame' || cat === 'structure_guide';
}

/**
 * Estrae profondità (mm) di un profilo per la sezione: usa `material.thickness`
 * se popolato (es. profili Knauf con campo dedicato), altrimenti `material.width`
 * (per profili Fassa con sezione codificata nel nome) o thickness del layer.
 *
 * Fallback finale: 27mm (profilo C Plus 27/50/27 standard controsoffitti).
 */
function profileDepthMm(layer: LayerV2): number {
  const m = layer.material;
  return Number(m?.thickness) || Number(m?.width) || Number(layer.thickness) || 27;
}

/**
 * Stima il numero di pendini per m² in base allo spacing primario × perpendicolare.
 * Es. spacing 900 × 900 → 1 pendino ogni 0.81 m² → ~1.23 pendini/m².
 */
export function estimateHangerCountPerSqm(
  hangerSpacingPrimaryMm: number,
  hangerSpacingPerpendicularMm: number,
): number {
  const areaPerHangerSqm = (hangerSpacingPrimaryMm / 1000) * (hangerSpacingPerpendicularMm / 1000);
  if (areaPerHangerSqm <= 0) return 0;
  return Math.round((1 / areaPerHangerSqm) * 100) / 100;
}

// ============================================================================
// Build model
// ============================================================================

/**
 * Costruisce il modello sezione controsoffitto.
 *
 * Layer iteration logic:
 *  1. Inserisce SEMPRE SlabBlock in cima (datum, non un layer raw).
 *  2. Se attachment_type='suspended' E ceiling_drop_mm > 0, inserisce HangerSpaceBlock.
 *  3. Itera i layer raw:
 *     - structure_frame/structure_guide → FrameBlock (level dipende dall'ordine: 1° = primary, 2° = secondary).
 *     - insulation → CeilingInsulationBlock (position dipende dall'ordine).
 *     - board / ceiling_tile → BoardBlock (riusato da wallModel).
 *     - altre categorie → skip silente (presenti per calcolo costi, non per sezione).
 */
export function buildCeilingSectionModel(
  layers: LayerV2[],
  ceiling: CeilingDimensionalState,
): CeilingSectionModel {
  const blocks: CeilingBlock[] = [];

  // 1. Solaio (datum)
  blocks.push({
    kind: 'slab',
    thicknessMm: 80,
    label: 'Solaio esistente',
  });

  // 2. Camera tecnica + pendini (solo se pendinato)
  const isSuspended = ceiling.attachmentType === 'suspended';
  const hasDrop = isSuspended && ceiling.ceilingDropMm > 0;
  if (hasDrop) {
    blocks.push({
      kind: 'hanger_space',
      depthMm: ceiling.ceilingDropMm,
      hangerSpacingPrimaryMm: ceiling.hangerSpacingPrimaryMm,
      hangerSpacingPerpendicularMm: ceiling.hangerSpacingPerpendicularMm,
      label: `Camera tecnica ${ceiling.ceilingDropMm} mm — pendini ${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm} mm`,
    });
  }

  // 3. Itera layer
  let frameLevelCounter: 0 | 1 | 2 = 0; // 0 = primary in attesa, 1 = secondary in attesa, 2 = saturato
  let firstFrameSeen = false;
  let boardCount = 0;

  for (const layer of layers) {
    const cat = layer.material?.category;

    if (isProfileType(layer)) {
      const level: 'primary' | 'secondary' = firstFrameSeen ? 'secondary' : 'primary';
      const cls = classifyMaterial(layer);
      const depthMm = profileDepthMm(layer);
      const spacingMm = level === 'primary' ? ceiling.primarySpacingMm : (ceiling.secondarySpacingMm ?? 500);

      blocks.push({
        kind: 'frame',
        level,
        depthMm,
        layer,
        spacingMm,
        fillId: cls.fillId || 'grad-metal',
        label: layer.material?.name ?? (level === 'primary' ? 'Orditura primaria' : 'Orditura secondaria'),
      });
      firstFrameSeen = true;
      if (frameLevelCounter < 2) frameLevelCounter = (frameLevelCounter + 1) as 0 | 1 | 2;
      continue;
    }

    if (cat === 'insulation') {
      const cls = classifyMaterial(layer);
      const position: CeilingInsulationBlock['position'] =
        frameLevelCounter >= 2 ? 'in_secondary_gap' : 'above_primary';
      blocks.push({
        kind: 'ceiling_insulation',
        layer,
        depthMm: Number(layer.thickness) || 0,
        fillId: cls.fillId,
        label: cls.label,
        position,
      });
      continue;
    }

    if (cat === 'board' || cat === 'ceiling_tile') {
      const cls = classifyMaterial(layer);
      blocks.push({
        kind: 'board',
        layer,
        thicknessMm: Number(layer.thickness) || 0,
        materialKind: cls.kind,
        fillId: cls.fillId,
        label: cls.label,
      });
      boardCount++;
      continue;
    }

    // accessory / screw / finish / other → skip nel modello visuale (presenti nei calcoli costi)
  }

  // Totale profondità (escluso slab — è il datum, non parte della stratigrafia)
  const totalDepthMm = blocks.reduce((sum, b) => {
    if (b.kind === 'slab') return sum;
    if (b.kind === 'board') return sum + b.thicknessMm;
    if (b.kind === 'hanger_space') return sum + b.depthMm;
    if (b.kind === 'frame') return sum + b.depthMm;
    if (b.kind === 'ceiling_insulation') return sum + b.depthMm;
    return sum;
  }, 0);

  return {
    blocks,
    totalDepthMm,
    hasDoubleStructure: frameLevelCounter >= 2,
    isSuspended,
    hangerCountPerSqm: isSuspended
      ? estimateHangerCountPerSqm(ceiling.hangerSpacingPrimaryMm, ceiling.hangerSpacingPerpendicularMm)
      : null,
    boardCount,
  };
}

// ============================================================================
// Title generator
// ============================================================================

/**
 * Genera un titolo dinamico stile catalogo per il controsoffitto.
 * Es:
 *  - "Controsoffitto a contatto" (attachment_type='fixed')
 *  - "Controsoffitto pendinato orditura singola"
 *  - "Controsoffitto pendinato orditura doppia"
 *  - "Controsoffitto REI 60 a protezione antincendio"
 *  - "Controsoffitto fonoassorbente"
 */
export function generateCeilingTitle(model: CeilingSectionModel): string {
  if (model.boardCount === 0) return 'Controsoffitto in configurazione';

  // Detect fire boards (REI/EI compartimentazione)
  const hasFireBoard = model.blocks.some(b =>
    b.kind === 'board' && (b.materialKind === 'board_fire' || b.materialKind === 'board_high_density'),
  );
  // Detect acoustic boards (Cleaneo, Gyptone forate)
  const hasAcousticBoard = model.blocks.some(b =>
    b.kind === 'board' && b.materialKind === 'board_perforated',
  );
  // Detect pannelli a vista (ceiling_tile)
  const hasCeilingTile = model.blocks.some(b =>
    b.kind === 'board' && b.layer.material?.category === 'ceiling_tile',
  );

  if (!model.isSuspended) return 'Controsoffitto a contatto';

  const structure = model.hasDoubleStructure ? 'orditura doppia' : 'orditura singola';

  if (hasFireBoard) return `Controsoffitto REI ${structure}`;
  if (hasAcousticBoard) return `Controsoffitto fonoassorbente ${structure}`;
  if (hasCeilingTile) return `Controsoffitto modulare a vista ${structure}`;

  return `Controsoffitto pendinato ${structure}`;
}
